import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { allowsOpenAIAds, getOpenAIAdsChoice } from "@/lib/openaiAdsPrivacy";
import { OpenAIAdsConsent } from "./OpenAIAdsConsent";

const PIXEL_ID = "NoAqde7zmhc8SScExMX7A2";
type PixelQueue = ((...args: unknown[]) => void) & { q?: unknown[][] };
const pixelWindow = () => window as Window & { oaiq?: PixelQueue };
let configuration: Promise<boolean> | undefined;

// The provider can enable automatic matching remotely. Do not initialize without
// confirming it is OFF: no customer matching was authorized for this rollout.
async function matchingDisabled(): Promise<boolean> {
  configuration ??= fetch(`https://bzrcdn.openai.com/pixel-config/v1/${PIXEL_ID}.json`, {
    signal: AbortSignal.timeout(2000), credentials: "omit",
  }).then(async response => response.ok && (await response.json()).automatic_advanced_matching_enabled === false)
    .catch(() => false);
  return configuration;
}

export function OpenAIAdsPixel() {
  const { pathname } = useLocation();
  const excluded = /^\/(admin|auth|sign|customer|booking)(\/|-|$)/.test(pathname);
  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      const choice = await getOpenAIAdsChoice();
      if (cancelled) return;
      if (excluded || !allowsOpenAIAds(choice)) {
        pixelWindow().oaiq?.("consent", false);
        return;
      }
      if (!await matchingDisabled() || cancelled) return;
      const latest = await getOpenAIAdsChoice();
      if (cancelled || !allowsOpenAIAds(latest)) return;
      if (!pixelWindow().oaiq) {
        const queue: PixelQueue = (...args) => { queue.q?.push(args); };
        queue.q = [];
        pixelWindow().oaiq = queue;
        queue("consent", true);
        queue("init", { pixelId: PIXEL_ID, debug: false });
        const script = document.createElement("script");
        script.id = "openai-ads-pixel";
        script.async = true;
        script.src = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
        document.head.appendChild(script);
      }
      pixelWindow().oaiq?.("consent", true);
      // No browser lead event: the server owns lead_created to avoid duplicates.
    };
    void sync();
    window.addEventListener("storage", sync);
    window.addEventListener("openai-ads-choice-change", sync);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", sync);
      window.removeEventListener("openai-ads-choice-change", sync);
    };
  }, [pathname, excluded]);
  return <OpenAIAdsConsent excluded={excluded} />;
}